import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getLenguajes } from '../api/client';
import { usePageMeta } from '../lib/usePageMeta';

const CACHE_KEY = 'labpsi_lenguajes_cache';

/** Títulos de cards como en el sitio original (por ID). */
const LABELS = {
  1: 'Ejercicios para Estudiantes',
  4: 'Ejercicios para la Comunidad',
};

/** Imágenes locales de fallback (réplica de /Images/l_*.png). */
const FALLBACK_IMG = {
  1: '/assets/img/l_1.png',
  4: '/assets/img/l_4.png',
};

/** Home en español: Comunidad + Estudiantes. El inglés (ID 2) se abre con el link English. */
const ORDER = [4, 1];

/** Estilo de hover: cognitivo (violeta) vs pedagógico (amarillo). */
const CARD_CLASS = {
  4: 'cogni',
  1: 'pedag',
};

function cardVariant(l) {
  if (CARD_CLASS[l.ID]) return CARD_CLASS[l.ID];
  const d = String(l.Descripcion ?? '').toLowerCase();
  if (/cognit|comunidad|community/.test(d)) return 'cogni';
  if (/pedag|estudiant|student/.test(d)) return 'pedag';
  return '';
}

/** Orden de pantallas al pulsar flecha abajo (como el sitio viejo). */
const SECTION_IDS = {
  1: 'section-1',
  2: 'que-es',
  3: 'quienes',
  4: 'agradecimientos',
  5: 'volver-arriba',
};
const LAST_SECTION = 5;

export default function Index() {
  usePageMeta('LABPSI - Ejercicios Cognitivos');
  const navigate = useNavigate();
  const [lenguajes, setLenguajes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') ?? []; }
    catch { return []; }
  });
  const hadCacheRef = useRef(lenguajes.length > 0);
  const [loading, setLoading] = useState(!hadCacheRef.current);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(1);
  const [showUp, setShowUp] = useState(false);
  const activeSectionRef = useRef(1);

  useEffect(() => {
    getLenguajes()
      .then((data) => {
        setLenguajes(data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        setError(null);
      })
      .catch((e) => { if (!hadCacheRef.current) setError(e.message); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Evitar restauración de scroll (Brave/Chrome) que deja la flecha al revés
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    activeSectionRef.current = 1;
    setActiveSection(1);
    setShowUp(false);

    const updateFromScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const viewH = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      // Solo "cerca del final" si realmente se puede scrollear (evita falso positivo al montar)
      const canScroll = docH > viewH + 150;
      const nearBottom = canScroll && scrollY + viewH >= docH - 80;

      setShowUp(nearBottom);

      const offset = 160;
      let current = 1;
      for (let i = LAST_SECTION; i >= 1; i--) {
        const el = document.getElementById(SECTION_IDS[i]);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = i;
          break;
        }
      }
      if (nearBottom) current = LAST_SECTION;
      if (current !== activeSectionRef.current) {
        activeSectionRef.current = current;
        setActiveSection(current);
      }
    };

    // Recalcular cuando el layout ya tiene altura real (imágenes / tipografía)
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => updateFromScroll())
      : null;
    if (ro) ro.observe(document.documentElement);

    window.addEventListener('scroll', updateFromScroll, { passive: true });
    // Doble rAF: tras el primer layout estable
    requestAnimationFrame(() => requestAnimationFrame(updateFromScroll));

    return () => {
      window.removeEventListener('scroll', updateFromScroll);
      ro?.disconnect();
    };
  }, []);

  // Como el sitio viejo: English no es card del home; el link va a Pedagogical Exercises (lenguaje 2).
  const englishLenguaje = lenguajes.find((l) => l.ID === 2 || l.idioma === 'en');

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-600 text-lg">Cargando...</p>
      </div>
    );
  }

  // Si la API falla y no hay caché, mostrar las dos cards clásicas para no romper el diseño
  const fallbackCards = [
    { ID: 4, Descripcion: LABELS[4], Textobtn: 'INGRESAR', idioma: 'es' },
    { ID: 1, Descripcion: LABELS[1], Textobtn: 'INGRESAR', idioma: 'es' },
  ];
  const cards = (() => {
    // Solo accesos en español en el home (excluye Pedagogical Exercises / en)
    const source = lenguajes.filter((l) => l.ID !== 2 && (l.idioma ?? 'es') !== 'en');
    if (!source.length) return error ? fallbackCards : [];
    const ordered = ORDER.map((id) => source.find((l) => l.ID === id)).filter(Boolean);
    return ordered.length ? ordered : source;
  })();

  if (error && !cards.length) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const scrollTo = (id, sectionNum) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (!el && sectionNum !== LAST_SECTION) return;
    if (sectionNum === LAST_SECTION || id === 'volver-arriba') {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    } else {
      const top = el.getBoundingClientRect().top + window.scrollY - 150;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
    if (sectionNum) {
      activeSectionRef.current = sectionNum;
      setActiveSection(sectionNum);
    }
  };

  const goSection = (num) => {
    const next = Math.min(LAST_SECTION, Math.max(1, num));
    scrollTo(SECTION_IDS[next], next);
  };

  // Preferir ilustraciones clásicas (l_4 / l_1) para igualar el sitio original
  const cardImage = (l) =>
    FALLBACK_IMG[l.ID] || l.icono || '/assets/img/logo.png';

  return (
    <div className="page-index index">
      <div className="navbar-wrapper">
        <div className={`menu-top${menuOpen ? ' menu-open' : ''}`}>
          <a href="/" className="logo-container">
            <img className="logo" src="/assets/img/logo.png" alt="" />
            <div>
              <img className="logo-letra" src="/assets/img/logo-letra-blanco.png" alt="LABPSI" />
              <div className="logo-inicio">INICIO</div>
            </div>
          </a>

          <button
            type="button"
            className="navbar-toggle"
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            aria-controls="index-navbar-nav"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="icon-bar" />
            <span className="icon-bar" />
            <span className="icon-bar" />
          </button>

          <nav
            id="index-navbar-nav"
            className={`navbar-nav${menuOpen ? ' is-open' : ''}`}
            aria-label="Secciones"
          >
            <button
              type="button"
              className={activeSection === 2 ? 'active' : ''}
              onClick={() => scrollTo('que-es', 2)}
            >
              ¿QUÉ ES EL LABPSI?
            </button>
            <button
              type="button"
              className={activeSection === 3 ? 'active' : ''}
              onClick={() => scrollTo('quienes', 3)}
            >
              ¿QUIENES SOMOS?
            </button>
            <button
              type="button"
              className={activeSection === 4 ? 'active' : ''}
              onClick={() => scrollTo('agradecimientos', 4)}
            >
              AGRADECIMIENTOS
            </button>
            <Link
              to="/terminos"
              className="nav-mobile-extra"
              onClick={() => setMenuOpen(false)}
            >
              Términos y condiciones
            </Link>
            <a
              href="mailto:labpsi@mdp.edu.ar"
              className="nav-mobile-extra"
              onClick={() => setMenuOpen(false)}
            >
              CONTACTO
            </a>
          </nav>

          <div className="header-utils">
            <div className="lang-switch">
              <button
                type="button"
                className="active"
                onClick={() => navigate(`/lenguaje/${englishLenguaje?.ID ?? 2}`)}
              >
                English
              </button>
            </div>
            <Link to="/terminos" className="link-terminos link-terminos-desktop">
              Términos y condiciones
            </Link>
          </div>
        </div>

        <div className="menu-top-contact">
          <a href="mailto:labpsi@mdp.edu.ar">
            <img src="/assets/img/contact.png" alt="" />
            CONTACTO
          </a>
        </div>
      </div>

      <div className="marketing">
        <div id="section-1" className="section-1 full-h">
          <div className="tests-container">
            <div className="tests-row">
              {cards.map((l) => (
                <a
                  key={l.ID}
                  href="#"
                  className={`lenguaje-item ${cardVariant(l)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/lenguaje/${l.ID}`);
                  }}
                >
                  <img className="img-languaje" src={cardImage(l)} alt="" />
                  <h2>{LABELS[l.ID] ?? l.Descripcion}</h2>
                  <span className="btn-ingresar">{l.Textobtn ?? 'INGRESAR'}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div id="que-es" className="section-2 featurette full-h">
          <div className="sections-container">
            <h2>¿QUÉ ES EL LABPSI?</h2>
            <p>
              Este laboratorio tiene un doble propósito. Por un lado, tiene una función pedagógica y busca servir como apoyo didáctico para los/as alumnos/as de la carrera de Psicología. Por otro lado, tiene un apartado destinado a la comunidad general con ejercicios cognitivos que permite que las personas exploren tareas que ponen en juego distintos procesos cognitivos.
            </p>
          </div>
        </div>

        <div id="quienes" className="section-3 featurette full-h">
          <div className="sections-container">
            <h2>¿QUIÉNES SOMOS?</h2>
            <div className="quienes-logos">
              <a
                href="https://ipsibat.conicet.gov.ar/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="IPSIBAT"
              >
                <img
                  src="/assets/img/logo-IPSIBAT-CONICET-UNMdP.png"
                  alt="IPSIBAT CONICET UNMdP"
                  style={{ height: 90, width: 'auto' }}
                />
              </a>
              <a
                href="https://grupogamamdq.org/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GAMA"
              >
                <img
                  src="/assets/img/logo-GAMA-2.jpeg"
                  alt="GAMA"
                  style={{ height: 60, width: 'auto' }}
                />
              </a>
            </div>
            <p>
              Somos docentes e investigadores de la Facultad de Psicología de la Universidad Nacional de Mar del Plata, pertenecientes al Instituto de Psicología Básica, Aplicada y Tecnología{' '}
              <a href="https://ipsibat.conicet.gov.ar/" target="_blank" rel="noopener noreferrer">(IPSIBAT)</a>, que desarrollamos nuestra actividad docente en las cátedras de Teorías del Aprendizaje y Psicología Cognitiva. En la parte de Ejercicios Cognitivos trabajamos en estrecha colaboración con el Centro Integral de la Memoria{' '}
              <a href="https://grupogamamdq.org/" target="_blank" rel="noopener noreferrer">GAMA</a>.
              <br /><br />
              <small>Integrantes:</small>
              <br />
              Dra. Leticia Vivas, Esp. Patricia Butrón, Lic. Juliana Ramella, Lic. Araceli Revollo Sarmiento, Lic. María Celeste López Moreno, Lic. Axel Fernández Zaionz, Lic. Estela Benítez, Lic. en TO Rocío Vera.
            </p>
          </div>
        </div>

        <div id="agradecimientos" className="section-4 featurette full-h">
          <div className="sections-container">
            <h2>FINANCIAMIENTO</h2>
            <p>- Association for Psychological Science (APS) por medio del Fondo para la Enseñanza y Comprensión Pública de la Ciencia Psicológica. 2016</p>
            <p>- Charles Matthew Fund otorgado por la International Neuropsychological Society (INS). 2024-2025</p>
            <p>- Secretaría de Ciencia y Técnica de la Universidad Nacional de Mar del Plata. 2021-2022 y 2023-2024</p>
            <p>- Proyectos de Tecnologías para la Inclusión Social, Ministerio de Ciencia y Técnica. 2022.</p>
            <br /><br />
            <h2>AGRADECIMIENTOS</h2>
            <p>- Se agradece la participación de los concurrentes a GAMA y los Centros de Jubilados Fé, Esperanza y Dignidad; Peralta Ramos Oeste y Centro de Día Las Tertulias en el rediseño del laboratorio.</p>
            <p>- Se agradece la participación de los concurrentes a la Unidad Gerontológica de la Municipalidad de General Pueyrredón por su participación en la validación de nuevos ejercicios.</p>
            <p>- Se agradecen también a todos l@s profesionales que nos derivan participantes para las distintas instancias del proyecto.</p>
          </div>
        </div>

        <div id="volver-arriba" className="section-final">
          <button type="button" className="icono-home-new" onClick={() => goSection(1)}>
            <img src="/assets/img/flecha-volver.png" alt="" />
            <br />VOLVER
            <br />ARRIBA
          </button>
          <div style={{ clear: 'both' }} />
        </div>
      </div>

      {!showUp && (
        <button
          type="button"
          className="icono-abajo"
          onClick={() => goSection(activeSectionRef.current + 1)}
          aria-label="Desplazar hacia abajo"
        >
          <img src="/assets/img/abajoNew.png" alt="" />
        </button>
      )}
      {showUp && (
        <button
          type="button"
          className="icono-arriba"
          onClick={() => goSection(Math.max(1, activeSectionRef.current - 1))}
          aria-label="Desplazar hacia arriba"
        >
          <img src="/assets/img/arribaNew.png" alt="" />
        </button>
      )}
    </div>
  );
}
